<script setup lang="ts">
import { computed } from 'vue'

import { useReportCellFormatter } from '../../composables/useReportCellFormatter'
import type { ReportColumn } from '../../models/column.types'
import type { ReportFilter } from '../../models/filter.types'
import type { ReportRow } from '../../models/report.types'

import audienceNetworkLogo from '@/shared/assets/platforms/audience_network.png'
import facebookLogo from '@/shared/assets/platforms/facebook.svg.png'
import instagramLogo from '@/shared/assets/platforms/instagram.webp'
import messengerLogo from '@/shared/assets/platforms/messenger.png'
import threadsLogo from '@/shared/assets/platforms/threads.png'
import { useI18n } from '@/shared/i18n'
import { Button, PlatformLogo, resolveStaticPlatformLogo } from '@/shared/ui'
import { MetricView } from '@/shared/ui/metric-view'

// The tile's dimension + metric strip — v3 `OrphexMetricDisplay.vue` (227 ln) minus the block that
// moved elsewhere: column ordering/exclusion is now the pure `orderCardColumns` service.
//
// EVERY value goes through `useReportCellFormatter` (ORP-18651 AC3). v3 called it for metrics only
// and printed raw `data[dim.field]` for dimensions, so dates and workspace ids rendered unformatted
// on a card while the table beside it formatted them.
const props = withDefaults(
  defineProps<{
    row: ReportRow
    dimensions: readonly ReportColumn[]
    metrics: readonly ReportColumn[]
    /** Marketing channel this surface is pinned to — a logo-less metric borrows it. */
    channel?: string
    filterable?: boolean
  }>(),
  { channel: '', filterable: false },
)
const emit = defineEmits<{ 'add-filter': [filter: ReportFilter] }>()

const PUBLISHER_PLATFORM_LOGOS: Readonly<Record<string, string>> = {
  audience_network: audienceNetworkLogo,
  facebook: facebookLogo,
  instagram: instagramLogo,
  messenger: messengerLogo,
  threads: threadsLogo,
}

const { formatCell } = useReportCellFormatter()
const { t } = useI18n()

/** `formatCell` renders a missing value as `-`; v3 dropped the whole row instead (`:113-115`). */
function present(columns: readonly ReportColumn[]): { column: ReportColumn; value: string }[] {
  return columns
    .map((column) => ({ column, value: formatCell(props.row[column.field], column) }))
    .filter((entry) => entry.value !== '-')
}

const presentedDimensions = computed(() => present(props.dimensions))
const presentedChronology = computed(() =>
  present([
    { field: 'first_seen', header: t('reports.card.firstSeen'), sub_type: 'date' },
    { field: 'last_seen', header: t('reports.card.lastSeen'), sub_type: 'date' },
    { field: 'active_days', header: t('reports.card.activeDays'), numeric: true },
  ]),
)
const presentedMetrics = computed(() => present(props.metrics))

const publisherPlatforms = computed(() => {
  const value = props.row['publisher_platforms']
  if (typeof value !== 'string') return []
  return value
    .split(',')
    .map((platform) => platform.trim())
    .filter(Boolean)
    .map((platform) => ({
      platform,
      src: PUBLISHER_PLATFORM_LOGOS[platform.toLowerCase()] ?? resolveStaticPlatformLogo(platform),
    }))
})

function addFilter(column: ReportColumn): void {
  emit('add-filter', { field: column.field, match_mode: 'equals', value: props.row[column.field] })
}
</script>

<template>
  <dl class="report-media-metrics grid m-0 pt-0 px-0 pb-lg gap-y-sm gap-x-md grid-cols-2">
    <div
      v-if="publisherPlatforms.length"
      class="report-media-metrics__row report-media-metrics__row--dimension grid min-w-0 items-center gap-xs grid-cols-[minmax(0,_7.5rem)_minmax(0,_1fr)] col-span-full"
      data-testid="report-media-publisher-platforms"
    >
      <dt class="report-media-metrics__label--dimension">
        {{ t('reports.card.publisherPlatforms') }}
      </dt>
      <dd class="flex items-center gap-xs">
        <PlatformLogo
          v-for="item in publisherPlatforms"
          :key="item.platform"
          :platform="item.platform"
          v-bind="item.src ? { src: item.src } : {}"
          :alt="item.platform"
          :size="20"
        />
      </dd>
    </div>
    <div
      v-for="entry in presentedDimensions"
      :key="entry.column.field"
      class="report-media-metrics__row report-media-metrics__row--dimension group relative grid min-w-0 items-center gap-xs grid-cols-[minmax(0,_7.5rem)_minmax(0,_1fr)] col-span-full"
    >
      <dt class="report-media-metrics__label--dimension">
        {{ t('reports.card.dimensionLabel', { label: entry.column.header }) }}
      </dt>
      <dd :title="entry.value">{{ entry.value }}</dd>
      <Button
        v-if="filterable"
        class="absolute right-0 top-1/2 -translate-y-1/2 opacity-0 transition group-hover:opacity-100 focus-visible:opacity-100 motion-reduce:transition-none"
        size="xs"
        variant="soft"
        density="compact"
        :aria-label="t('reports.card.filterBy', { label: entry.column.header })"
        data-testid="report-media-filter"
        @click="addFilter(entry.column)"
      >
        {{ t('reports.card.filter') }}
      </Button>
    </div>
    <div
      v-for="entry in presentedChronology"
      :key="entry.column.field"
      class="report-media-metrics__row report-media-metrics__row--metric grid min-w-0 items-center gap-xs grid-cols-[minmax(0,_7.5rem)_minmax(0,_1fr)]"
    >
      <dt>{{ entry.column.header }}</dt>
      <dd>{{ entry.value }}</dd>
    </div>
    <div
      v-for="entry in presentedMetrics"
      :key="entry.column.field"
      class="report-media-metrics__row report-media-metrics__row--metric grid min-w-0 items-center gap-xs grid-cols-[minmax(0,_7.5rem)_minmax(0,_1fr)]"
    >
      <dt class="flex min-w-0 items-center gap-2xs">
        <MetricView :column="entry.column" :channel="channel" only-logo :size="14" />
        <span class="min-w-0 truncate">{{
          t('reports.card.metricLabel', { label: entry.column.header })
        }}</span>
      </dt>
      <dd>{{ entry.value }}</dd>
    </div>
  </dl>
</template>

<style scoped>
.report-media-metrics dt {
  overflow: hidden;
  color: var(--ui-color-fg);
  font-size: 0.75rem;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.report-media-metrics dt.report-media-metrics__label--dimension {
  overflow: visible;
  text-overflow: clip;
  white-space: normal;
}
.report-media-metrics dd {
  margin: 0;
  overflow: hidden;
  color: var(--ui-color-fg-soft);
  font-size: 0.75rem;
  font-weight: 300;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
